import type LivingMap from '../map';
import type LivingMapPlugin from './base-plugin';

/**
 * The aim for this class is to maintain and manage the lifecyle of the map plugins.
 *
 * More broadly, map plugins should be used when:
 * - a piece of logic required map lifecycle events to be used
 * - needs access / control over some layers on the map
 * - wants to add / mutate data on the map.
 *
 * @class PluginHelper
 */
class PluginHelper {
  private map: LivingMap;
  private plugins = new Set<LivingMapPlugin>();

  constructor(coreMappingInstance: LivingMap) {
    this.map = coreMappingInstance;
  }

  /**
   * Adds a constructed plugin class.
   * @param  {LivingMapPlugin} pluginInstance
   * @returns void
   */
  public add(pluginInstance: LivingMapPlugin): void {
    this.plugins.add(pluginInstance);

    // if the map style is already loaded, we activate it straight away.
    // Otherwise we won't and well do it once the style load event is propagated.
    if (this.map.hasStyledLoaded()) {
      pluginInstance.activate();
    }
  }

  /**
   * @param id 
   */
  public getById(id: string): LivingMapPlugin | undefined {
    const pluginSet = Array.from(this.plugins);
    const plugin = pluginSet.find(plugin => plugin.getId() === id);
    return plugin;
  }

  /**
   * Removes a constructed plugin class.
   * @param  {LivingMapPlugin} pluginInstance
   * @returns void
   */
  public remove(pluginInstance: LivingMapPlugin): void {
    this.plugins.delete(pluginInstance);

    if (pluginInstance.activated) {
      pluginInstance.deactivate();
    }
  }

  /**
   * deactivates any plugin and then removes all of them from the storage. meant to use for teardown only.
   * @returns void
   */
  public clear(): void {
    this.deactivateAll();
    this.plugins.clear();
  }

  /**
   * To de-activate a plugin the plugin essentially shuts down. 
   * It cleans up all its logic and it will stop lisiting for any events.
   */
  public deactivateAll(): void {
    this.plugins.forEach((plugin) => {
      if (plugin.activated) {
        plugin.deactivate();
        plugin.activated = false;
      }
    });
  }

  /**
   * To activate a plugin essentially means that the plugin can start using all LivingMap methodes. 
   * It can start setting listeners, add / altering layers & sources, etc.
   */
  public activateAll(): void {
    this.plugins.forEach((plugin) => {
      if (!plugin.activated) {
        plugin.activate();
        plugin.activated = true;
      }
    });
  }

  /**
   * A refresh usually means that a new stylesheet is initialsed. 
   * For a plugin that might mean re-applying its filters or re-applying the feature-state that has been set.
   */
  public refreshAll(): void {
    this.plugins.forEach((plugin) => {
      if (plugin.activated) plugin.refresh();
    });
  }
}

export default PluginHelper;
